import { useState, useEffect, useMemo } from 'react';
import { useUserStore } from '../../store/models';
import { useHistory } from 'react-router-dom';
import { getPhoneVerificationDismissed, setPhoneVerificationDismissed } from '../../utils';

/**
 * Determines whether the phone verification banner should be shown.
 * Exported for testability.
 */
export function shouldShowBanner({ attributesLoaded, isFederated, phoneNumberVerified }) {
    if (!attributesLoaded) return false;
    if (isFederated) return false;
    // Cognito may return the value as boolean true or string 'true'
    return phoneNumberVerified !== true && phoneNumberVerified !== 'true';
}

function usePhoneVerificationBanner() {
    const userStore = useUserStore();
    const history = useHistory();

    const [userAttributes, setUserAttributes] = useState(null);
    const [attributesLoaded, setAttributesLoaded] = useState(false);
    const [isFederated, setIsFederated] = useState(false);
    const userEmail = userStore.user?.email;
    const [dismissed, setDismissed] = useState(() => getPhoneVerificationDismissed(userEmail));

    // Re-check dismissed state once userEmail resolves (on refresh it may be undefined on first render)
    useEffect(() => {
        if (userEmail) {
            setDismissed(getPhoneVerificationDismissed(userEmail));
        }
    }, [userEmail]);

    // Fetch attributes on mount if not already loaded in the store
    useEffect(() => {
        if (userStore.user && !userStore.user.userAttributes) {
            userStore.getUserDataFromCognito();
        }
        // eslint-disable-next-line
    }, []);

    // Sync attributes from store whenever they are updated
    useEffect(() => {
        if (userStore.user?.userAttributes) {
            setUserAttributes(userStore.user.userAttributes);
            setAttributesLoaded(true);
        }
        // eslint-disable-next-line
    }, [userStore.observed.userAttributes]);

    // Sync federated status whenever the user object changes
    useEffect(() => {
        if (userStore.user) {
            setIsFederated(Boolean(userStore.user.isFederated));
        }
        // eslint-disable-next-line
    }, [userStore.observed.user]);

    const showBanner = useMemo(
        () =>
            !dismissed &&
            shouldShowBanner({
                attributesLoaded,
                isFederated,
                phoneNumberVerified: userAttributes?.phone_number_verified,
            }),
        [attributesLoaded, isFederated, userAttributes, dismissed],
    );

    function handleVerifyNow() {
        history.push('/admin/settings/security');
    }

    function handleDismiss() {
        setPhoneVerificationDismissed(userEmail);
        setDismissed(true);
    }

    return { showBanner, handleVerifyNow, handleDismiss };
}

export default usePhoneVerificationBanner;
